import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getBlogPreviews } from './blogUtils';

// Self-hosted blog images have stored downscaled variants; the stacked
// mobile card shows the image near full width, so ask for the 640px one.
// External media URLs pass through untouched.
const withThumb = (url) =>
  url && url.includes('/api/blog-images/') ? `${url}?w=640` : url;

// How many cards to show before "Load More".
const INITIAL_VISIBLE = 6;

// Title Styling
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '2.4rem',
  marginTop: '22px',
  marginBottom: '10px',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '1.2rem',
  },
}));

// Orange Divider Styling
const OrangeDividerBar = styled(motion('div'))(({ theme }) => ({
  width: '80%',
  height: '3.5px',
  backgroundColor: '#FF6B00',
  margin: '14px auto 18px',
  transformOrigin: 'center',
  position: 'relative',
  zIndex: 1,
  boxShadow: `
    0 0 5px #FF8F00,
    0 0 10px #FF6B00,
    0 0 20px #FF6B00,
    0 0 30px #FF4500,
    0 0 50px #FF4500
  `,
  [theme.breakpoints.down('sm')]: {
    width: '95%',
  },
}));

// Blog card container. Not clickable itself — only the Read More link
// navigates — but it hovers with the same sweeping shine as the Top Crypto
// Lenders cards.
const BlogCard = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '14px',
  backgroundColor: '#1a237e',
  padding: '12px 14px',
  borderRadius: '16px',
  color: '#FFFFFF',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  background: `
    linear-gradient(135deg,
      rgba(26, 35, 126, 0.95) 0%,
      rgba(21, 101, 192, 0.9) 100%
    )
  `,
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.03) 0px,
        rgba(255, 255, 255, 0.03) 1px,
        transparent 1px,
        transparent 2px
      )
    `,
    pointerEvents: 'none',
  },
  // Shine strip that sweeps across on hover (same as the loan cards)
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
    transform: 'translateX(-100%)',
    transition: 'transform 0.5s ease',
    pointerEvents: 'none',
  },
  boxShadow: '0 4px 15px rgba(0,0,0,0.2), inset 0 0 30px rgba(26,35,126,0.2)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.3), inset 0 0 35px rgba(26,35,126,0.3)',
    '&:after': {
      transform: 'translateX(100%)',
      transition: 'transform 1s ease',
    },
  },
  // On phones the card stacks: full-width image on top, text below.
  // One combined sm block (duplicate object keys silently overwrite), and
  // height must be auto or the grid's 100% height clips the text.
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '10px',
    padding: '12px',
    borderRadius: '8px',
    height: 'auto',
  },
}));

const BlogImage = styled('img')(({ theme }) => ({
  width: '116px',
  height: '82px',
  objectFit: 'cover',
  borderRadius: '8px',
  border: '1px solid #FFFFFF',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '150px',
  },
}));

const BlogTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  fontWeight: 'bold',
  color: '#FFFFFF',
  marginBottom: '8px',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
  },
}));

const BlogMetadata = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#FF6B00',
  marginBottom: '8px',
  textShadow: '0 0 3px rgba(255,107,0,0.5)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
  },
}));

const BlogPreview = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.9)',
  marginTop: '2px',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
  lineHeight: '1.45',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

// The only clickable part of the card: a real router link to the article.
const ReadMoreCue = styled(Link)(({ theme }) => ({
  color: '#FF6B00',
  marginTop: '8px',
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '0.65rem',
  letterSpacing: '1px',
  textShadow: '0 0 6px #FF8F00, 0 0 14px rgba(255, 143, 0, 0.45)',
  textDecoration: 'none',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  textAlign: 'left',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.2s ease, text-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateX(3px)',
    textShadow: '0 0 10px #FF8F00, 0 0 22px rgba(255, 143, 0, 0.8)',
  },
  '&:focus-visible': {
    outline: '3px solid #FFD54F',
    outlineOffset: '3px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
    letterSpacing: '0.5px',
  },
}));

// Retro pill: solid white border + cyan glow, matching the site's blue/cyan
// scheme (the old orange outline clashed and read as unfinished).
const LoadMoreButton = styled('button')(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '0.85rem',
  cursor: 'pointer',
  color: '#FFFFFF',
  padding: '14px 28px',
  borderRadius: '999px',
  border: '3px solid #FFFFFF',
  backgroundColor: '#1a237e',
  boxShadow: '0 0 12px rgba(0, 191, 255, 0.6), 0 4px 0 rgba(0, 0, 0, 0.55)',
  textShadow: '0 0 6px rgba(0, 191, 255, 0.8)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#00BFFF',
    color: '#0B1020',
    textShadow: 'none',
    transform: 'translateY(-2px)',
    boxShadow: '0 0 20px rgba(0, 191, 255, 0.9), 0 6px 0 rgba(0, 0, 0, 0.55)',
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: '0 0 12px rgba(0, 191, 255, 0.6), 0 2px 0 rgba(0, 0, 0, 0.55)',
  },
  '&:focus-visible': {
    outline: '2px solid #FFD54F',
    outlineOffset: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: '12px 20px',
  },
}));

const EmptyState = styled(Typography)(({ theme }) => ({
  color: 'rgba(255,255,255,0.85)',
  textAlign: 'center',
  padding: '40px 16px',
  fontSize: '1.1rem',
}));

const NewsAndBlogs = () => {
  // Derived once from the single source of truth (already sorted newest-first).
  const previews = useMemo(() => getBlogPreviews(), []);

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const visible = previews.slice(0, visibleCount);
  const hasMore = previews.length > visibleCount;

  return (
    <div>
      <SectionTitle>News &amp; Blogs</SectionTitle>

      <OrangeDividerBar
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 2, ease: 'easeInOut' }}
      />

      {previews.length === 0 ? (
        <EmptyState>No articles yet — check back soon.</EmptyState>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 1.5, md: 2 },
            padding: { xs: 1.5, md: 3 },
            maxWidth: '1400px',
            margin: '0 auto',
            alignItems: 'stretch',
          }}
        >
          {visible.map((blog, index) => (
            <BlogCard
              key={blog.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(index, 3) * 0.05 }}
            >
              <BlogImage
                src={withThumb(blog.image)}
                alt={blog.title}
                referrerPolicy="no-referrer"
                loading="lazy"
              />
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                <BlogMetadata>
                  {blog.tagline} • {blog.date}
                  {blog.author ? ` • by ${blog.author}` : ''}
                </BlogMetadata>
                <BlogTitle>{blog.title}</BlogTitle>
                <BlogPreview>{blog.preview}</BlogPreview>
                <ReadMoreCue
                  to={`/info-center/${blog.slug}`}
                  aria-label={`Read article: ${blog.title}`}
                >
                  Read More →
                </ReadMoreCue>
              </Box>
            </BlogCard>
          ))}
        </Box>
      )}

      {hasMore && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            // Clear space + own stacking layer so the animated blog cards
            // (transformed during their entrance) can never paint over the
            // button or crowd its top edge.
            mt: { xs: 3, md: 2 },
            mb: 4,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <LoadMoreButton
            type="button"
            onClick={() => setVisibleCount((c) => c + INITIAL_VISIBLE)}
          >
            Load More
          </LoadMoreButton>
        </Box>
      )}
    </div>
  );
};

export default NewsAndBlogs;
